import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { WireGuardTunnel } from "../wireGuardTunnelsInterfaces";
import {
  DEFAULT_ONVIF_PORT,
  DEFAULT_RTSP_PORT,
  FIELD_LABEL_SX,
  MONO,
  MULISH,
  PANEL_BORDER,
  SECTION_LABEL_SX,
  TARGET_PANEL_WIDTH_PX,
} from "./vpnDebugConstants";
import { VpnDebugCameraCandidate } from "./vpnDebugInterfaces";
import { useVpnDebuggerStore } from "./vpnDebuggerStore/vpnDebuggerStore";
import { SHADED_SURFACE_BG } from "../../Troubleshooting/TroubleshootingUi";
import { fetchCameraCredentials } from "./VpnDebuggerUtils/fetchCameraCredentials";

interface Props {
  tunnels: WireGuardTunnel[];
  getServerName: (serverId: number | null) => string;
  onRun: () => void;
}

const selectSx = {
  ...MULISH,
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-root, & .MuiSelect-select": { backgroundColor: "#fff" },
  "& *": { fontFamily: "Mulish" },
};

const fieldSx = {
  ...MULISH,
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
  "& *": { fontFamily: "Mulish" },
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box>
    <Typography sx={{ ...FIELD_LABEL_SX, mb: 0.5 }}>{label}</Typography>
    {children}
  </Box>
);

const cameraLabel = (camera: VpnDebugCameraCandidate) => {
  const name = camera.camera_name || `Camera ${camera.camera_id}`;
  const site = camera.customer_name ? ` · ${camera.customer_name}` : "";
  return `${name} — ${camera.ip}${site}`;
};

export const VpnDebugTargetForm = ({
  tunnels,
  getServerName,
  onRun,
}: Props) => {
  const {
    tunnelId,
    setTunnelId,
    targets,
    targetsLoading,
    selectedCameraId,
    setSelectedCameraId,
    host,
    setHost,
    rtspPort,
    setRtspPort,
    onvifPort,
    setOnvifPort,
    username,
    setUsername,
    password,
    setPassword,
    credentialSource,
    credentialMasked,
    credentialEdited,
    applyPrefilledCredential,
    running,
    reset,
  } = useVpnDebuggerStore();

  const allCandidates = React.useMemo(
    () => [...(targets?.cameras || []), ...(targets?.other_cameras || [])],
    [targets]
  );
  const selectedCamera =
    allCandidates.find((entry) => entry.camera_id === selectedCameraId) || null;

  const handleCameraChange = (rawValue: string) => {
    if (!rawValue) {
      setSelectedCameraId(null);
      return;
    }
    const cameraId = Number(rawValue);
    setSelectedCameraId(cameraId);
    const camera = allCandidates.find((entry) => entry.camera_id === cameraId);
    if (!camera) return;
    setHost(camera.ip || "");
    setRtspPort(String(camera.rtsp_port || DEFAULT_RTSP_PORT));
    setOnvifPort(String(camera.http_port || DEFAULT_ONVIF_PORT));

    // Prefill the credential plane B would actually use, resolved camera -> site
    // exactly as the server does. applyPrefilledCredential deliberately does not
    // set credentialEdited, so an untouched prefill still runs down the
    // camera_id path where the server reads the secret itself.
    void fetchCameraCredentials(cameraId, camera.customer_id).then((found) => {
      if (useVpnDebuggerStore.getState().selectedCameraId !== cameraId) return;
      applyPrefilledCredential(found);
    });
  };

  const credentialNote = () => {
    if (credentialEdited) {
      return {
        text: "Overridden - this run uses what you typed instead of the stored credential.",
        color: "#b25e00",
      };
    }
    if (credentialMasked) {
      return {
        text: `A ${credentialSource} credential is stored but hidden - you lack permission to view passwords. The run still uses it.`,
        color: "#6b7280",
      };
    }
    if (credentialSource === "camera") {
      return { text: "From this camera's settings.", color: "#1e7d34" };
    }
    if (credentialSource === "site") {
      return {
        text: "Inherited from the site - this camera has none of its own.",
        color: "#1e7d34",
      };
    }
    return {
      text: "Nothing stored on the camera or the site. Enter credentials to run authenticated checks.",
      color: "#b25e00",
    };
  };

  // MUI's Select matches `value` against a flat child list, so the two groups are
  // built as one array with ListSubheader separators rather than nested nodes.
  const cameraOptions: React.ReactNode[] = [
    <MenuItem key="__manual" value="" sx={MULISH}>
      — enter a target manually —
    </MenuItem>,
  ];
  if (targets?.cameras.length) {
    cameraOptions.push(
      <ListSubheader
        key="__in_range"
        sx={{ ...MULISH, fontWeight: 700, fontSize: 12 }}
      >
        In this tunnel&apos;s routed ranges
      </ListSubheader>
    );
    targets.cameras.forEach((camera) => {
      cameraOptions.push(
        <MenuItem key={camera.camera_id} value={camera.camera_id} sx={MULISH}>
          {cameraLabel(camera)}
        </MenuItem>
      );
    });
  }
  if (targets?.other_cameras.length) {
    // Kept available, not hidden: subnet metadata goes stale and virtually
    // assigned ranges are real, so a strict filter would hide exactly the camera
    // being chased.
    cameraOptions.push(
      <ListSubheader
        key="__out_of_range"
        sx={{ ...MULISH, fontWeight: 700, fontSize: 12 }}
      >
        Outside the routed ranges
      </ListSubheader>
    );
    targets.other_cameras.forEach((camera) => {
      cameraOptions.push(
        <MenuItem key={camera.camera_id} value={camera.camera_id} sx={MULISH}>
          {cameraLabel(camera)}
        </MenuItem>
      );
    });
  }

  return (
    <Paper
      sx={{
        width: TARGET_PANEL_WIDTH_PX,
        minWidth: TARGET_PANEL_WIDTH_PX,
        height: "100%",
        maxHeight: "100%",
        borderRadius: 2,
        borderTop: PANEL_BORDER,
        backgroundColor: SHADED_SURFACE_BG,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ...MULISH,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          height: 46,
          flexShrink: 0,
          borderBottom: PANEL_BORDER,
          backgroundColor: SHADED_SURFACE_BG,
        }}
      >
        <Typography sx={{ ...MULISH, fontWeight: 700, fontSize: 14 }}>
          TARGET
        </Typography>
        <Box
          sx={{
            transform: "scale(0.82)",
            transformOrigin: "right center",
            mr: "-10px",
          }}
        >
          <OrangeTextButton
            onClick={() => reset()}
            text="Reset"
            disabled={running}
          />
        </Box>
      </Box>

      <Box sx={{ px: 2, py: 2, overflowY: "auto", flex: 1, minHeight: 0 }}>
        <Typography sx={{ ...SECTION_LABEL_SX, mb: 1 }}>Gateway</Typography>
        <Field label="Tunnel">
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              inputProps={{ "aria-label": "Tunnel" }}
              value={tunnelId ?? ""}
              onChange={(event) => {
                const nextValue = event.target.value;
                setTunnelId(nextValue === "" ? null : Number(nextValue));
              }}
              disabled={running}
              sx={selectSx}
            >
              <MenuItem value="" sx={MULISH}>
                — select a tunnel —
              </MenuItem>
              {tunnels.map((tunnel) => (
                <MenuItem key={tunnel.id} value={tunnel.id} sx={MULISH}>
                  {tunnel.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Field>

        {tunnelId != null && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ ...MULISH, fontSize: 12, color: "#6b7280" }}>
              {getServerName(
                tunnels.find((t) => t.id === tunnelId)?.server ?? null
              )}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "wrap",
                mt: 0.75,
              }}
            >
              <Typography sx={{ ...MULISH, fontSize: 12, color: "#6b7280" }}>
                Routed:
              </Typography>
              {targetsLoading && <CircularProgress size={13} />}
              {!targetsLoading && targets?.scan_targets.length
                ? targets.scan_targets.map((cidr) => (
                    <Box
                      key={cidr}
                      component="span"
                      sx={{
                        ...MONO,
                        fontSize: 11,
                        backgroundColor: "#eef1f5",
                        border: "1px solid #dde2e9",
                        borderRadius: "4px",
                        px: 0.75,
                        py: "1px",
                        color: "#3c4858",
                      }}
                    >
                      {cidr}
                    </Box>
                  ))
                : !targetsLoading && (
                    <Typography
                      sx={{
                        ...MULISH,
                        fontSize: 12,
                        color: "#b3261e",
                        fontStyle: "italic",
                      }}
                    >
                      none configured
                    </Typography>
                  )}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ ...SECTION_LABEL_SX, mb: 1 }}>Device</Typography>

        <Field label="Camera">
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              inputProps={{ "aria-label": "Camera" }}
              value={selectedCameraId ?? ""}
              onChange={(event) => {
                const nextValue = String(event.target.value ?? "");
                handleCameraChange(nextValue);
              }}
              disabled={running || tunnelId == null}
              sx={selectSx}
            >
              {cameraOptions}
            </Select>
          </FormControl>
        </Field>

        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <Box sx={{ flex: 1.6 }}>
            <Field label="Host">
              <TextField
                fullWidth
                size="small"
                placeholder="172.16.1.50"
                value={host}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setHost(nextValue);
                }}
                disabled={running}
                sx={fieldSx}
              />
            </Field>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Field label="RTSP">
              <TextField
                fullWidth
                size="small"
                value={rtspPort}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setRtspPort(nextValue);
                }}
                disabled={running}
                sx={fieldSx}
              />
            </Field>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Field label="ONVIF">
              <TextField
                fullWidth
                size="small"
                value={onvifPort}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setOnvifPort(nextValue);
                }}
                disabled={running}
                sx={fieldSx}
              />
            </Field>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ ...SECTION_LABEL_SX }}>Credentials</Typography>
        {selectedCamera ? (
          <Typography
            sx={{
              ...MULISH,
              fontSize: 12,
              mt: 0.5,
              mb: 1.25,
              color: credentialNote().color,
            }}
          >
            {credentialNote().text}
          </Typography>
        ) : (
          <Typography
            sx={{
              ...MULISH,
              fontSize: 12,
              color: "#6b7280",
              mt: 0.5,
              mb: 1.25,
            }}
          >
            Used for the ONVIF and RTSP checks. Nothing typed here is saved to
            the camera.
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Field label="Username">
              <TextField
                fullWidth
                size="small"
                autoComplete="off"
                value={username}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setUsername(nextValue);
                }}
                disabled={running}
                sx={fieldSx}
              />
            </Field>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Field label="Password">
              <TextField
                fullWidth
                size="small"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setPassword(nextValue);
                }}
                disabled={running}
                sx={fieldSx}
              />
            </Field>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 2, py: 1.5, flexShrink: 0, borderTop: PANEL_BORDER }}>
        <Button
          fullWidth
          variant="contained"
          disableElevation
          onClick={onRun}
          disabled={running || tunnelId == null}
          startIcon={
            running ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : undefined
          }
          sx={{
            ...MULISH,
            textTransform: "none",
            fontWeight: 700,
            fontSize: 14,
            py: 1,
            backgroundColor: "#FF8400",
            "&:hover": { backgroundColor: "#E67300" },
          }}
        >
          {running ? "Running…" : "Run diagnostics"}
        </Button>
        <Typography
          sx={{
            ...MULISH,
            fontSize: 11.5,
            color: "#6b7280",
            mt: 1,
            textAlign: "center",
          }}
        >
          Tunnel checks run first (~15s), then the device checks.
        </Typography>
      </Box>
    </Paper>
  );
};
